import type { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import React from "react";

import { isModuleSimpleTextSection, isModuleListSection } from "@calcom/contentful/type-guards";
import { getTopic } from "@calcom/features/topics/lib/getTopic";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@calcom/ui";

import { Markdown } from "@lib/Markdown";
import { getRelatedTopics } from "@calcom/features/topics/lib/getRelatedTopics";

import { TopicsList } from "@calcom/features/topics/components/TopicsList";
import type { nonEmptyBooking } from "@calcom/features/experts/lib/getBookingPropsForExpersIds";
import { getBookingPropsForExpersIds } from "@calcom/features/experts/lib/getBookingPropsForExpersIds";
import { getTopics } from "@calcom/features/topics/lib/getTopics";
import { BookingSection } from "@calcom/features/bookings/components/BookingSection";
import { Hero } from "@components/ui/Hero";
import { getTopicsWithExperts } from "@calcom/features/experts/lib/getTopicsWithExperts";
import { getExperts } from "@calcom/features/experts/lib/getExperts";
import { getSchedule } from "@calcom/trpc/server/routers/viewer/slots";
import prisma from "@calcom/prisma";
import { getNextAvailableSlotForTopics } from "helpers/getNextAvailableSlotForTopics";
import Head from "next/head";

export type TopicPageProps = InferGetStaticPropsType<typeof getStaticProps>;
function TopicPage({ topic, relatedTopics, booking: rawBooking, slots, topics }: TopicPageProps) {
  const { t } = useLocale();
  const nextAvailableSlotForTopics = getNextAvailableSlotForTopics(topics, slots);

  const booking = !rawBooking.hasOwnProperty("notFound") ? (rawBooking as nonEmptyBooking) : null;

  return (
    <>
      <Head>
        <title>NoA Consultations</title>
        <meta
          name="description"
          content="This is NoA Consultations topic page, where you can find more informations about our specializations "
        />
      </Head>

      <Hero color="gray">
        <div className="container relative">
          <div className="max-w-2xl py-10 md:py-32">
            {!!topic?.tags?.length && (
              <div className="flex items-center gap-3">
                {topic?.tags?.map((tag) => (
                  <p key={tag.sys.id} className="bg-brand-100 text-md mb-3 block px-2 py-1">
                    #{tag.fields.name}
                  </p>
                ))}
              </div>
            )}
            <h1 className="text-5xl md:text-6xl">{topic.name}</h1>
            <p className="my-4 text-xl leading-normal">{topic.description}</p>

            {booking && (
              <>
                <p className="my-4 text-xl leading-normal">
                  Running time: 60 minutes
                  <br />
                  Price: <span className="line-through">£120</span>{" "}
                  <span className="font-bold">NOW FREE (limited time offer)</span>
                </p>
                <Button size="2xl" href="#book-now">
                  {t("book")}
                </Button>
              </>
            )}
          </div>
        </div>
      </Hero>
      <main>
        {topic?.content?.map((item) => {
          if (isModuleSimpleTextSection(item)) {
            return (
              <div
                key={item.sys.id}
                className="container flex flex-col gap-10 py-12 md:grid md:md:grid md:grid-cols-12 md:py-32">
                <div className="col-span-12 md:col-span-5">
                  <Markdown>{item?.fields?.sectionHeader?.fields?.header}</Markdown>
                </div>
                {item?.fields?.text && typeof item?.fields?.text === "string" && (
                  <div className="col-span-12 max-w-2xl text-xl leading-normal md:col-span-7">
                    <Markdown>{item.fields.text}</Markdown>
                  </div>
                )}
              </div>
            );
          } else if (isModuleListSection(item)) {
            return (
              <div key={item.sys.id} className="bg-brand-500 text-white">
                <div
                  key={item.sys.id}
                  className="container flex flex-col gap-10 py-10 md:grid md:md:grid md:grid-cols-12 md:py-32">
                  <div className="col-span-12 lg:col-span-5">
                    <Markdown>{item?.fields?.header?.fields?.header}</Markdown>
                  </div>
                  <ul className="col-span-12 grid gap-10 md:grid-cols-2 lg:col-span-7">
                    {/* @ts-expect-error contentful types are wrong */}
                    {item?.fields?.listItems?.map((listItem) => (
                      <li key={listItem.sys.id} className="flex flex-col gap-5 border-t pt-5">
                        <h3 className="text-3xl">{listItem.fields.header}</h3>
                        <p className="text-xl leading-normal">{listItem.fields.text}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          }
        })}
        {booking && <BookingSection booking={booking} />}
        {!!relatedTopics.length && (
          <TopicsList
            header="Similar topics"
            topics={relatedTopics}
            booking={booking}
            topicNextAvailableSlot={nextAvailableSlotForTopics}
          />
        )}
      </main>
    </>
  );
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const slug = Array.isArray(context?.params?.slug) ? context?.params?.slug.pop() : context?.params?.slug;

  if (!slug) return { notFound: true } as { notFound: true };

  const topic = await getTopic(slug);

  if (!topic) return { notFound: true } as { notFound: true };

  // @ts-expect-error contentful types are wrong
  const tags = topic?.tags?.flatMap((tag) => tag?.fields?.name ?? []) ?? [];

  const relatedTopics = await getRelatedTopics(tags, [topic.id]);

  const experts = await getExperts();
  const expertsList = experts.map((expert) => expert.username);

  const topics = await getTopics();
  const topicsWithExperts = getTopicsWithExperts(topics, experts);

  const booking =
    experts.length > 0
      ? await getBookingPropsForExpersIds(
          experts
            .filter((expert) => expert.topics.map((t) => t.id).includes(topic.id))
            .map((expert) => expert.id)
        )
      : { notFound: true };

  const expertsWithTopics = getTopicsWithExperts(relatedTopics, experts);

  const { slots } = await getSchedule(
    {
      usernameList: expertsList as string[],
      startTime: new Date().toISOString(),
      endTime: new Date(new Date().setMonth(new Date().getMonth() + 2)).toISOString(),
      duration: 60,
      eventTypeId: booking && "eventType" in booking ? booking.eventType.id : 5,
      eventTypeSlug: booking && "eventType" in booking ? booking.eventType.slug : "consultation",
      timeZone: "Europe/Warsaw",
      debug: true,
    },
    { prisma }
  );

  return {
    props: {
      booking,
      topic,
      relatedTopics: expertsWithTopics,
      slots,
      topics: topicsWithExperts,
    },
    revalidate: 60,
  };
};

export const getStaticPaths = async () => {
  const topics = await getTopics();

  const paths = topics.map((topic) => ({
    params: { slug: topic.slug },
  }));

  return {
    paths,
    fallback: "blocking",
  };
};

export default TopicPage;
